import { z } from "zod";

import { sanitizeInput } from "@/helpers/helpers";

import type { Writer } from "../types/writer";

export const SONG_TITLE_MAX_LENGTH = 60;

export function createAlbumSongFormSchema(isTunecore: boolean, availableWriters: Writer[]) {
  return z.object({
    songTitle: z
      .string()
      .min(1, "Song title is required")
      .max(SONG_TITLE_MAX_LENGTH, `Song title must be ${SONG_TITLE_MAX_LENGTH} characters or less`)
      .refine((value) => {
        const sanitized = sanitizeInput(value);
        const safeSanitized = sanitized || "";
        return safeSanitized === value;
      }, "Song title contains invalid characters"),

    durationMins: z
      .number()
      .min(0, "Duration minutes cannot be negative")
      .max(60, "Duration minutes must be less than 60"),

    durationSecs: z
      .number()
      .min(0, "Duration seconds cannot be negative")
      .max(59, "Duration seconds must be less than 60"),

    alternativeTitles: z
      .array(z.string())
      .refine((values) => {
        return values.every((value) => {
          if (!value || value.trim() === "")
            return true;
          const sanitized = sanitizeInput(value);
          const safeSanitized = sanitized || "";
          return safeSanitized === value;
        });
      }, "One or more alternative titles contain invalid characters"),

    performingArtists: z
      .array(z.string().min(1, "Performing artist name is required").max(100, "Artist name must be less than 100 characters"))
      .min(1, "At least one performing artist is required")
      .refine((values) => {
        return values.every((value) => {
          const sanitized = sanitizeInput(value);
          const safeSanitized = sanitized || "";
          return safeSanitized === value;
        });
      }, "One or more performing artist names contain invalid characters"),

    writers: z
      .array(z.object({
        writercode: z.string(),
        writername: z.string(),
        writershare: z.number().min(0).max(100),
        writerdesignationcode: z.number().min(1).max(3),
        righttocollect: z.boolean(),
      }))
      .min(1, "At least one writer is required")
      .refine((writers) => {
        const totalShares = writers.reduce((sum, writer) => sum + writer.writershare, 0);
        return Math.abs(totalShares - 100) < 0.01;
      }, "Total writer shares must equal 100%")
      .refine((writers) => {
        return writers.some(writer => writer.righttocollect === true);
      }, "At least one writer must have 'Right To Collect' enabled")
      .refine((writers) => {
        if (!isTunecore)
          return true;

        const writersWithRtc = writers.filter(writer => writer.righttocollect === true);

        for (const writer of writersWithRtc) {
          const fullWriter = availableWriters.find(w => w.writercode === writer.writercode);
          if (!fullWriter)
            continue;

          const hasDateOfBirth = Boolean(fullWriter.dateofbirth != null && fullWriter.dateofbirth.trim() !== "");
          const hasCaeIpi = Boolean(fullWriter.caeipinumber != null && fullWriter.caeipinumber.trim() !== "");
          const hasImroRegistrationPending = Boolean(fullWriter.writerexternalregistrationstatus != null);

          if (hasImroRegistrationPending) {
            if (!hasDateOfBirth) {
              return false;
            }
          }
          else if (!hasDateOfBirth || !hasCaeIpi) {
            return false;
          }
        }

        return true;
      }, "For Tunecore submissions, writers with 'Right To Collect' must have Date of Birth. Writers without pending IMRO registration also require IPI/CAE number"),
  });
}

export type AlbumSongFormData = z.infer<ReturnType<typeof createAlbumSongFormSchema>>;
